import { z } from 'zod';
import { PLATFORM_IDS, PLATFORM_REGISTRY, } from '../platform-registry';
import { mcpRiskLevelLabels, transportLabels } from '../mcp';
import { shadowAiCategoryLabels } from '../shadow-ai';
/**
 * The public vocabularies shared across `/api/v1` (KIR-4432).
 *
 * ## Why this file exists
 *
 * KIR-4431 shipped ten resources that each published an internal slug where the
 * product shows a display name — `claude` for "Claude Code", `stdio_local` for
 * "Command + Local Files", `code_assist` for "Code Assistant". A customer
 * comparing a response to the screen they are looking at saw two different
 * vocabularies, which is the confusion this file removes.
 *
 * Two rules, applied to every vocabulary below:
 *
 * 1. **The public value is lowercase-hyphenated**, per `NAMING.md`. Never an
 *    internal slug, never a raw database value.
 * 2. **Wherever the UI shows a name, the response carries both** — the stable
 *    key *and* the human-readable label, as siblings. That is `NAMING.md`'s
 *    publish-both rule, which before this pass was applied nowhere.
 *
 * ## Why the labels are imported rather than restated
 *
 * Every label here already exists in exactly one place that the frontend renders
 * from — `PLATFORM_REGISTRY.displayName`, `transportLabels`,
 * `shadowAiCategoryLabels`. Restating them would create a second copy that
 * drifts the first time a display name changes, and the drift would be invisible
 * (both sides compile, the API just starts disagreeing with the screen again).
 * So this module *derives*, and a label change in the registry reaches the API
 * without anyone editing it.
 */
// ---------------------------------------------------------------------------
// Platforms
// ---------------------------------------------------------------------------
/**
 * Registry key → public platform id.
 *
 * The registry keys are lowercase alphanumerics plus `_`, and none contains a
 * `-`, so `_`→`-` is a **bijection** and {@link toInternalPlatformId} can invert
 * it without a second table. That property is what lets a public filter value be
 * translated back to the internal column value at the DTO boundary.
 */
export function toPublicPlatformId(internalId) {
    return internalId.split('_').join('-');
}
/** Public platform id → the internal registry key. Inverse of {@link toPublicPlatformId}. */
export function toInternalPlatformId(publicId) {
    return publicId.split('-').join('_');
}
/**
 * The platform ids this API publishes, derived from the registry.
 *
 * `claude_desktop` becomes `claude-desktop`; single-word ids such as `cursor`
 * and `vscode` are unchanged.
 */
export const V1_PLATFORM_IDS = PLATFORM_IDS.map(toPublicPlatformId);
/**
 * Public platform id → display name, the label the product puts on the screen.
 *
 * Sourced from `PLATFORM_REGISTRY.displayName`, which is what
 * `platformLabels` in the frontend renders — so `claude` reads "Claude Code"
 * here exactly as it does in the app.
 */
export const V1_PLATFORM_NAMES = Object.freeze(Object.fromEntries(PLATFORM_IDS.map((id) => [
    toPublicPlatformId(id),
    PLATFORM_REGISTRY[id].displayName,
])));
/**
 * Platforms that are not registry entries but do appear as a chip on a screen.
 *
 * `git` is the only one: git-family hooks carry no `idePlatform`, and the Hooks
 * table renders them with a dedicated "Git" chip (`GitBadge`). Publishing
 * `platform: null` for those rows — which is what the contract did before this
 * pass — left a customer unable to reproduce a column the screen shows, with no
 * hint that the answer was "Git".
 */
export const V1_PSEUDO_PLATFORM_NAMES = Object.freeze({ git: 'Git' });
/**
 * Title-case an unknown platform id for display.
 *
 * Mirrors the frontend's `getPlatformLabel` fallback so an id neither side
 * recognises still renders the same way in both places.
 */
function titleCasePlatformId(publicId) {
    return publicId.charAt(0).toUpperCase() + publicId.slice(1).toLowerCase();
}
/**
 * Resolve the display name for a platform id.
 *
 * Precedence deliberately matches the frontend's `getIdeClientDisplayLabel`:
 * **a known Kirin platform always wins over a caller-supplied name.** Shadow AI
 * reports looser names for tools it correlates to a Kirin platform — "Claude"
 * where the product says "Claude Code" — and the app discards those. If this
 * API did not, `inventory-users` would publish a name the screen never shows.
 *
 * `observedName` is that looser Shadow AI name, used only for a tool with no
 * registry entry, where it is the best label anyone has.
 */
export function resolvePlatformName(publicId, observedName) {
    return (V1_PLATFORM_NAMES[publicId] ??
        V1_PSEUDO_PLATFORM_NAMES[publicId] ??
        observedName ??
        titleCasePlatformId(publicId));
}
/**
 * One IDE or coding agent, as `{ id, name }`.
 *
 * **One shape on every resource.** Before this pass `platforms` was
 * `string[]` on alerts / MCP servers / extensions / plugins and an object array
 * on inventory users — one field name with two types, which no generic client
 * can absorb. It is an object everywhere now, which is also `NAMING.md`'s rule
 * that a reference to another thing is an object rather than a bare id.
 *
 * `id` is typed `string` rather than an enum on purpose. For alerts, MCP
 * servers, extensions and plugins the underlying column *is* closed to the
 * registry — but `inventory-users` also reports tools Kirin does not support
 * (that is what `coverage: unknown` means), so a closed enum here would be a
 * promise the data breaks. The closed vocabulary is published on the **filter**
 * parameters instead, where it is true, and named in this field's description so
 * a caller still knows what to expect.
 */
export const v1PlatformSchema = z
    .object({
    id: z
        .string()
        .describe(`The platform identifier, stable and safe to pass to a \`platform\` filter. Normally one of: ${V1_PLATFORM_IDS.join(', ')}. Inventory resources may also report a tool Kirin does not support, which appears here as its observed identifier.`),
    name: z
        .string()
        .describe('The display name the Kirin app shows for this platform, such as "Claude Code" or "VS Code". Always present — an unrecognised identifier is title-cased rather than published as null.'),
})
    .meta({
    id: 'V1Platform',
    title: 'Platform',
    description: 'An IDE or coding agent, carrying both the stable identifier you filter on and the display name the Kirin app shows.',
    examples: [{ id: 'claude', name: 'Claude Code' }],
});
/** The closed platform vocabulary, for filter parameters whose column really is closed. */
export const v1PlatformIdFilterDescription = `Platform identifiers, as returned in \`platforms[].id\`. One of: ${V1_PLATFORM_IDS.join(', ')}.`;
// ---------------------------------------------------------------------------
// MCP transport
// ---------------------------------------------------------------------------
/**
 * Internal MCP transport → public id.
 *
 * The internal values are `stdio_local` / `stdio_repo` / `stdio_package`, and
 * the contract used to publish them unchanged on the stated grounds that "they
 * appear on the Inventory screen". They do not: `MCPTransportBadge` renders
 * `transportLabels[transport]`, so the screen says "Command + Local Files". The
 * published id is hyphenated per `NAMING.md` and the label ships beside it.
 */
export const V1_MCP_TRANSPORTS = Object.freeze({
    http: 'http',
    stdio_local: 'stdio-local',
    stdio_repo: 'stdio-repo',
    stdio_package: 'stdio-package',
});
/** Public transport id → internal, for translating a filter value back. */
export const V1_MCP_TRANSPORTS_TO_INTERNAL = Object.freeze(Object.fromEntries(Object.entries(V1_MCP_TRANSPORTS).map(([internal, publicId]) => [
    publicId,
    internal,
])));
/**
 * Public transport id → the label the Inventory screen shows.
 *
 * Derived from `transportLabels`, the map `MCPTransportBadge` renders, so the
 * two cannot disagree.
 */
export const V1_MCP_TRANSPORT_NAMES = Object.freeze(Object.fromEntries(Object.entries(V1_MCP_TRANSPORTS).map(([internal, publicId]) => [
    publicId,
    transportLabels[internal],
])));
export const v1McpTransportSchema = z
    .enum(Object.values(V1_MCP_TRANSPORTS))
    .meta({
    id: 'V1McpTransport',
    title: 'MCP transport',
    description: 'How the client reaches the server. `http` is a network endpoint; the `stdio-*` variants are a local command, launched from local files, from a repository checkout, or from an installed package. `transportName` carries the label the Kirin app shows for the same value.',
});
// ---------------------------------------------------------------------------
// MCP server AgentMesh risk level (KIR-5108)
// ---------------------------------------------------------------------------
/**
 * Internal AgentMesh risk level → public id. AgentMesh's own five buckets, in
 * AgentMesh's order; only `partial_scan` changes, hyphenated per `NAMING.md`.
 */
export const V1_MCP_RISK_LEVELS = Object.freeze({
    dangerous: 'dangerous',
    risky: 'risky',
    clean: 'clean',
    partial_scan: 'partial-scan',
    unscanned: 'unscanned',
});
/** Public risk level → internal, for translating a filter value back. */
export const V1_MCP_RISK_LEVELS_TO_INTERNAL = Object.freeze(Object.fromEntries(Object.entries(V1_MCP_RISK_LEVELS).map(([internal, publicId]) => [
    publicId,
    internal,
])));
/**
 * Public risk level → the label the Inventory screen shows. Derived from
 * `mcpRiskLevelLabels`, the map the Risk column renders, so the two cannot
 * disagree.
 */
export const V1_MCP_RISK_LEVEL_NAMES = Object.freeze(Object.fromEntries(Object.entries(V1_MCP_RISK_LEVELS).map(([internal, publicId]) => [
    publicId,
    mcpRiskLevelLabels[internal],
])));
export const v1McpRiskLevelSchema = z
    .enum(Object.values(V1_MCP_RISK_LEVELS))
    .meta({
    id: 'V1McpRiskLevel',
    title: 'MCP server risk level',
    description: 'The risk level AgentMesh assigns to an MCP server package release. Kirin reads it from AgentMesh and does not calculate it. `dangerous`: a serious risk was found. `risky`: some risk was found. `clean`: no risk was found. `partial-scan`: the scan is not finished. `unscanned`: AgentMesh has not scanned this release yet. `levelName` carries the label the Kirin app shows for the same value.',
});
// ---------------------------------------------------------------------------
// Shadow AI category
// ---------------------------------------------------------------------------
/**
 * Internal Shadow AI category → public id, and the label beside it.
 *
 * The internal keys are snake_case (`code_assist`, `llm_api`, `ai_browser`) and
 * the contract published them raw, as an open `z.string()`. Two problems in one
 * field: snake_case in a public body, and `llm_local` → "Local LLM" *inverts*
 * the word order, so even de-snake-casing the value client-side produces the
 * wrong label.
 *
 * The vocabulary is derived from `shadowAiCategoryLabels`, which is what
 * `getShadowAiCategoryLabel` renders in the app.
 */
export const V1_SHADOW_AI_CATEGORIES = Object.freeze(Object.fromEntries(Object.keys(shadowAiCategoryLabels).map(internal => [
    internal,
    toPublicPlatformId(internal),
])));
/** Public Shadow AI category id → internal, for translating a filter value back. */
export const V1_SHADOW_AI_CATEGORIES_TO_INTERNAL = Object.freeze(Object.fromEntries(Object.entries(V1_SHADOW_AI_CATEGORIES).map(([internal, publicId]) => [
    publicId,
    internal,
])));
/** Public Shadow AI category id → the label the Shadow AI screen shows. */
export const V1_SHADOW_AI_CATEGORY_NAMES = Object.freeze(Object.fromEntries(Object.entries(V1_SHADOW_AI_CATEGORIES).map(([internal, publicId]) => [
    publicId,
    shadowAiCategoryLabels[internal],
])));
export const V1_SHADOW_AI_CATEGORY_IDS = Object.values(V1_SHADOW_AI_CATEGORIES);
/**
 * Map an internal category to its public id.
 *
 * A category with no entry in the label map is hyphenated and passed through
 * rather than dropped: the set genuinely grows as new tools are classified, and
 * a null category would hide a row's whole classification. This is why the
 * published field stays a string with a documented known set rather than a
 * frozen enum — see {@link v1PlatformSchema} for the same argument.
 */
export function toPublicShadowAiCategory(internal) {
    return V1_SHADOW_AI_CATEGORIES[internal] ?? toPublicPlatformId(internal);
}
/** Resolve the display label for a public Shadow AI category id. */
export function resolveShadowAiCategoryName(publicId) {
    const known = V1_SHADOW_AI_CATEGORY_NAMES[publicId];
    if (known !== undefined) {
        return known;
    }
    // Mirrors the frontend's `formatUnknownShadowAiCategoryLabel`: split on the
    // separator, title-case each segment, and keep the two acronyms uppercase.
    return publicId
        .split('-')
        .map(segment => {
        const lower = segment.toLowerCase();
        if (lower === 'llm')
            return 'LLM';
        if (lower === 'api')
            return 'API';
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(' ');
}
//# sourceMappingURL=vocabulary.js.map